import React, { useCallback, useEffect, useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCruise } from "@/context/CruiseContext";
import { useAuth } from "@/context/AuthContext";
import { TESTIDS } from "@/lib/testIds";
import ProvisioningCard from "@/components/cruises/ProvisioningCard";

import StartChoice from "./StartChoice";
import PDFImport from "./PDFImport";
import ManualEntry from "./ManualEntry";
import ExtractionReview from "./ExtractionReview";
import { GROUPS, splitPayload, validateRequired } from "./fields";

/**
 * CreateCruiseWorkflow — the permanent orchestrator for cruise creation.
 *
 * Restored from the 2026-03-06 Product-Owner-approved workflow that was
 * deleted in commit `7eaf51d` (Foundation Freeze). Modernized to use
 * the canonical POST /api/voyages + PATCH /api/cruises/{id} endpoints
 * (never the retired /api/finance/cruise-bookings/*). Decomposed into
 * composable pieces per PO directive 2026-02-18.
 *
 * Stage machine:
 *   choice → pdf → review    → provisioning → done
 *          ↘ manual ↗
 *
 * ADR-CRUISE-001 §invariants:
 *   Invariant 5 — creation is strictly additive; POST /api/voyages
 *   inserts alongside existing voyages.
 *   Invariant 6 — deleting one cruise never affects another (this
 *   workflow only creates; deletion is a separate portfolio action).
 */

const INITIAL_GROUPS_OPEN = GROUPS.reduce((a, g) => ({ ...a, [g.key]: g.defaultOpen }), {});

export default function CreateCruiseWorkflow({ onClose, onCreated }) {
  const [stage, setStage] = useState("choice");
  // choice | pdf | manual | review | committing | provisioning | error
  const [fields, setFields] = useState({});
  const [lineId, setLineId] = useState(null);
  const [shipId, setShipId] = useState(null);
  const [lineCandidates, setLineCandidates] = useState([]);
  const [shipCandidates, setShipCandidates] = useState([]);
  const [filename, setFilename] = useState("");
  const [openGroups, setOpenGroups] = useState(INITIAL_GROUPS_OPEN);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [createdVoyageId, setCreatedVoyageId] = useState(null);

  const { user, refreshMe } = useAuth();
  const { setRoute, reloadVoyage, reloadToday, selectCruise, voyageMeta } = useCruise();

  const editField = useCallback((k, v) => {
    setFields((cur) => ({ ...cur, [k]: v }));
  }, []);

  const reset = () => {
    setStage("choice"); setFields({}); setLineId(null); setShipId(null);
    setLineCandidates([]); setShipCandidates([]); setFilename("");
    setOpenGroups(INITIAL_GROUPS_OPEN); setErrorMsg(""); setCreatedVoyageId(null);
  };

  const dismiss = () => {
    reset();
    if (onClose) onClose();
    else setRoute("voyages");
  };

  // --- PDF path ---
  const onExtracted = (data) => {
    setFields({ ...data.extracted });
    setLineId(data.resolved_line_id || data.line_id_candidates?.[0]?.id || null);
    setShipId(data.resolved_ship_id || data.ship_id_candidates?.[0]?.id || null);
    setLineCandidates(data.line_id_candidates || []);
    setShipCandidates(data.ship_id_candidates || []);
    setFilename(data.filename || "");
    setStage("review");
  };

  // --- Commit path (shared by manual + review) ---
  const submit = async () => {
    const err = validateRequired(fields);
    if (err) { setErrorMsg(err); return; }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const { voyage_create, voyage_patch } = splitPayload({
        fields,
        lineId,
        shipId,
        embarkPortId: fields.embark_port_id || null,
      });

      // ADR-CRUISE-001 · Invariant 5 — strictly additive.
      const createRes = await api.post("/api/cruises", voyage_create);
      const newId = createRes.data?.id;
      if (!newId) throw new Error("Voyage creation returned no id");

      // Enrichment. Failure here is non-fatal — cruise exists.
      if (Object.keys(voyage_patch).length > 0) {
        try {
          await api.patch(`/api/cruises/${newId}`, voyage_patch);
        } catch (patchErr) {
          console.warn("Voyage enrichment PATCH failed", patchErr);
        }
      }

      // Refresh downstream consumers.
      await refreshMe?.();
      // FIRST-CRUISE onboarding (2026-02-20 · PO): make the newly-created
      // cruise the app's currently-selected voyage. Without this, the
      // "Continue planning" button on <ProvisioningCard> lands the user
      // on whichever voyage was previously active (or nothing), instead
      // of the cruise they just built.
      try { await selectCruise?.(newId); } catch { /* selection is best-effort */ }
      await reloadVoyage?.();
      await reloadToday?.();

      setCreatedVoyageId(newId);
      if (onCreated) onCreated({ voyage_id: newId });
      // Concierge finish (still-live provisioning service).
      setStage("provisioning");
    } catch (e) {
      setErrorMsg(formatApiError(e));
      setStage("error");
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-scroll to top on stage transition.
  useEffect(() => {
    const el = document.querySelector('[data-testid="cc-create-cruise-modal"]');
    el?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [stage]);

  // Concierge full-viewport takeover.
  if (stage === "provisioning" && createdVoyageId) {
    return (
      <ProvisioningCard
        cruiseId={createdVoyageId}
        onComplete={() => {
          reset();
          if (onClose) onClose();
          else setRoute("voyage");
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/70 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
      onClick={dismiss}
      data-testid={TESTIDS.createCruiseModal}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl my-4 md:my-6 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader stage={stage} onClose={dismiss} />

        <div className="flex-1 overflow-y-auto">
        {stage === "choice" && (
          <StartChoice
            onUploadClick={() => setStage("pdf")}
            onManualClick={() => setStage("manual")}
          />
        )}

        {stage === "pdf" && (
          <PDFImport
            onExtracted={onExtracted}
            onCancel={() => setStage("choice")}
            onFallbackManual={() => setStage("manual")}
          />
        )}

        {stage === "manual" && (
          <ManualEntry
            fields={fields}
            editField={editField}
            openGroups={openGroups}
            setOpenGroups={setOpenGroups}
            onSubmit={submit}
            onBack={() => setStage("choice")}
            submitting={submitting}
            lineId={lineId}
            setLineId={setLineId}
            shipId={shipId}
            setShipId={setShipId}
          />
        )}

        {stage === "review" && (
          <ExtractionReview
            filename={filename}
            fields={fields}
            editField={editField}
            lineCandidates={lineCandidates}
            shipCandidates={shipCandidates}
            lineId={lineId}
            setLineId={setLineId}
            shipId={shipId}
            setShipId={setShipId}
            openGroups={openGroups}
            setOpenGroups={setOpenGroups}
            onSubmit={submit}
            onBack={() => setStage("pdf")}
            submitting={submitting}
          />
        )}

        {stage === "committing" && (
          <div className="px-10 py-16 text-center" data-testid={TESTIDS.createCruiseCommitting}>
            <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto" />
            <div className="font-display text-[20px] font-bold mt-5">Saving your cruise…</div>
            <div className="text-[13.5px] text-slate-500 mt-1.5">Almost there.</div>
          </div>
        )}

        {stage === "error" && (
          <div className="px-10 py-14 text-center" data-testid={TESTIDS.createCruiseError}>
            <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto" />
            <div className="font-display text-[20px] font-bold mt-4">We couldn't complete that.</div>
            <div className="text-[13.5px] text-slate-600 mt-2.5 max-w-md mx-auto">{errorMsg || "An unexpected error occurred."}</div>
            <div className="flex justify-center gap-3 mt-7">
              <button
                onClick={dismiss}
                className="px-5 py-2.5 text-[13.5px] font-semibold rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
                type="button"
              >
                Close
              </button>
              <button
                onClick={() => setStage("choice")}
                className="px-6 py-2.5 text-[13.5px] font-semibold rounded-full bg-amber-500 text-white hover:bg-amber-600"
                type="button"
                data-testid={TESTIDS.createCruiseErrorRetry}
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {errorMsg && stage !== "error" && (
          <div className="mx-10 my-3 p-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-[13px]" data-testid={TESTIDS.createCruiseInlineError}>
            {errorMsg}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function ModalHeader({ stage, onClose }) {
  const meta = {
    choice:     { eyebrow: "New Cruise",           title: "Add a cruise",              subtitle: "Choose how you'd like to add this trip to Cruise Command." },
    pdf:        { eyebrow: "Step · Upload",         title: "Upload your booking",       subtitle: "We'll extract every detail — you'll review before we save." },
    manual:     { eyebrow: "Guided Setup",          title: "Tell us about your cruise", subtitle: "A few quick questions — we'll do the rest." },
    review:     { eyebrow: "Step · Review",         title: "Here's what we found",      subtitle: "Confirm the details and Cruise Command takes it from here." },
    committing: { eyebrow: "Creating",              title: "Saving your cruise…",       subtitle: "One moment." },
    error:      { eyebrow: "Something went wrong",  title: "We couldn't complete that", subtitle: "You can try again — nothing has been saved." },
  }[stage] || { eyebrow: "New Cruise", title: "Add a cruise", subtitle: "" };
  return (
    <div className="px-10 py-7 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50/50 flex items-start justify-between gap-6 shrink-0">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-black tracking-[0.24em] uppercase text-amber-700">{meta.eyebrow}</div>
        <div className="font-display text-[26px] font-bold text-slate-900 mt-1 leading-tight" data-testid={TESTIDS.createCruiseTitle}>
          {meta.title}
        </div>
        {meta.subtitle && (
          <div className="text-[13.5px] text-slate-500 mt-1.5 max-w-2xl">{meta.subtitle}</div>
        )}
      </div>
      <button
        onClick={onClose}
        className="text-slate-400 hover:text-slate-700 p-2 -mr-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
        type="button"
        data-testid={TESTIDS.createCruiseClose}
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
